import { Button, EmptyState, Notice, formatDate } from "@omnabase/ui";
import type { BlockedSender } from "@omnabase/shared";
import { api, ApiError } from "../../lib/api";
import { useFetch } from "../../lib/inbox";
import { useWs } from "../../lib/nav";
import { useSession } from "../../shell/store";

/** Settings › Blocked senders (2026-10-02; brief spam-basics, draft 2): the addresses a person blocked by marking their ticket as
 *  spam. Mail from a blocked address is filed in Spam, never bounced; "Unblock" lets it reach the inbox again. A person sees the
 *  blocks set on the projects they may see. */
export function BlockedSenders() {
  const { slug } = useWs(); const { workspace, toast } = useSession();
  const list = useFetch<{ rows: BlockedSender[] }>(`/api/v1/w/${slug}/blocked-senders`);
  const rows = list.data?.rows ?? [];
  const multi = workspace.projects.filter((p) => !p.archivedAt).length > 1;
  const unblock = async (r: BlockedSender) => {
    try { await api.del(`/api/v1/w/${slug}/blocked-senders/${r.id}`); toast(`${r.email} is no longer blocked`, { kind: "success" }); await list.reload(); }
    catch (e) { toast(e instanceof ApiError ? e.message : "Could not unblock the sender", { kind: "error" }); }
  };
  return (
    <>
      <div className="ob-settings__head">
        <div><h1 className="ob-page__title">Blocked senders</h1><p className="ob-page__subtitle">A person's spam mark blocks a verified sender: their mail goes straight to Spam, with no draft, no notification and no bounce. The AI's spam marks block nobody.</p></div>
      </div>
      <div className="ob-listpage__scroll ob-dtable-card">
        <table className="ob-dtable">
          <thead><tr><th>Address</th>{multi && <th className="ob-dtable__col--phone">Project</th>}<th className="ob-dtable__col--phone">Blocked by</th><th className="ob-dtable__col--phone">Since</th><th /></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="ob-dtable__grow"><span className="ob-dtable__name" title={r.email}>{r.email}</span></td>
                {multi && <td className="ob-dtable__muted ob-dtable__col--phone">{workspace.projects.find((p) => p.id === r.projectId)?.name ?? ""}</td>}
                <td className="ob-dtable__muted ob-dtable__col--phone">{workspace.members.find((m) => m.userId === r.blockedBy)?.name ?? "A former member"}{r.ticketNumber ? ` · #${r.ticketNumber}` : ""}</td>
                <td className="ob-dtable__muted ob-dtable__col--phone">{formatDate(r.createdAt)}</td>
                <td className="ob-dtable__num"><Button size="sm" onClick={() => void unblock(r)} aria-label={`Unblock ${r.email}`}>Unblock</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {list.data && rows.length === 0 && <EmptyState icon="spam" title="No blocked senders" text="When a person marks a ticket as spam, its sender appears here. The AI's spam marks only move the ticket to Spam." />}
      </div>
      <Notice icon="info">A sender is only blocked when the mail's address is verified: a web form message, a forwarded mail or a customer with other tickets is marked as spam without a block.</Notice>
    </>
  );
}
